import styled, { keyframes, css } from 'styled-components';

// Animations
const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const ping = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  75%, 100% {
    transform: scale(2);
    opacity: 0;
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
`;

export const AuthContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1e1b4b 0%, #581c87 50%, #be185d 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  position: relative;
  overflow: hidden;
`;

export const BackgroundEffect = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
`;

interface BlurOrbProps {
  color: 'purple' | 'pink' | 'indigo';
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  delay?: string;
}

const getOrbColor = (color: string) => {
  switch (color) {
    case 'purple':
      return '#8B5CF6';
    case 'pink':
      return '#EC4899';
    case 'indigo':
      return '#6366F1';
    default:
      return '#8B5CF6';
  }
};

export const BlurOrb = styled.div<BlurOrbProps>`
  position: absolute;
  width: 20rem;
  height: 20rem;
  background-color: ${({ color }) => getOrbColor(color)};
  border-radius: 50%;
  mix-blend-mode: multiply;
  filter: blur(4rem);
  opacity: 0.7;
  animation: ${pulse} 4s ease-in-out infinite;
  animation-delay: ${({ delay }) => delay || '0s'};
  
  ${({ top }) => top && css`top: ${top};`}
  ${({ bottom }) => bottom && css`bottom: ${bottom};`}
  ${({ left }) => left && css`left: ${left};`}
  ${({ right }) => right && css`right: ${right};`}
`;

export const MainContent = styled.div`
  position: relative;
  z-index: 10;
  width: 100%;
  max-width: 28rem;
`;

export const BrandSection = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

export const LogoContainer = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 4rem;
  height: 4rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
  border-radius: 1rem;
  margin-bottom: 1rem;
  animation: ${float} 3s ease-in-out infinite;
`;

export const LogoImage = styled.img`
  width: 2rem;          
  height: 2rem;
  object-fit: contain; 
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2)); 
`;

export const BrandTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
  margin: 0 0 0.5rem 0;
  text-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
`;

export const BrandSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.125rem;
  margin: 0;
`;

export const AuthCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(16px);
  border-radius: 1.5rem;
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
`;

export const WelcomeSection = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

export const WelcomeTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: white;
  margin: 0 0 0.5rem 0;
`;

export const WelcomeSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.7);
  margin: 0;
`;

export const GoogleButton = styled.button`
  width: 100%;
  background: white;
  color: #1f2937;
  font-weight: 500;
  font-size: 1.125rem;
  padding: 1rem 1.5rem;
  border-radius: 1rem;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: scale(1);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);

  &:hover {
    background: #f9fafb;
    transform: scale(1.02);
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }

  &:active {
    transform: scale(0.98);
  }
`;

export const GoogleIcon = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  background: linear-gradient(45deg, #4285f4 0%, #ea4335 50%, #fbbc05 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ButtonText = styled.span`
  flex: 1;
`;

export const ZapIcon = styled.div`
  opacity: 0;
  transition: opacity 0.3s ease;
  color: #6366f1;
  
  ${GoogleButton}:hover & {
    opacity: 1;
  }
`;

export const FeaturesSection = styled.div`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: rgba(255, 255, 255, 0.8);
`;

interface FeatureIconProps {
  color: string;
}

export const FeatureIcon = styled.div<FeatureIconProps>`
  color: ${({ color }) => color};
  display: flex;
  align-items: center;
`;

export const FeatureText = styled.span`
  font-size: 0.875rem;
`;

export const Footer = styled.div`
  text-align: center;
  margin-top: 2rem;
`;

export const FooterText = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.875rem;
  margin: 0;
`;

export const FooterLink = styled.a`
  color: white;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

interface DecorativeCircleProps {
  size: string;
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  opacity: number;
}

export const DecorativeCircle = styled.div<DecorativeCircleProps>`
  position: absolute;
  width: ${({ size }) => size};
  height: ${({ size }) => size};
  border: 1px solid rgba(255, 255, 255, ${({ opacity }) => opacity});
  border-radius: 50%;
  pointer-events: none;
  
  ${({ top }) => top && css`top: ${top};`}
  ${({ bottom }) => bottom && css`bottom: ${bottom};`}
  ${({ left }) => left && css`left: ${left};`}
  ${({ right }) => right && css`right: ${right};`}
`;

interface DecorativeDotProps {
  size: string;
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  color: string;
  animation?: 'ping' | 'pulse';
}

export const DecorativeDot = styled.div<DecorativeDotProps>`
  position: absolute;
  width: ${({ size }) => size};
  height: ${({ size }) => size};
  background-color: ${({ color }) => color};
  border-radius: 50%;
  pointer-events: none;
  
  ${({ animation }) => {
    if (animation === 'ping') {
      return css`animation: ${ping} 1s cubic-bezier(0, 0, 0.2, 1) infinite;`;
    }
    if (animation === 'pulse') {
      return css`animation: ${pulse} 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;`;
    }
    return '';
  }}
  
  ${({ top }) => top && css`top: ${top};`}
  ${({ bottom }) => bottom && css`bottom: ${bottom};`}
  ${({ left }) => left && css`left: ${left};`}
  ${({ right }) => right && css`right: ${right};`}
`;

export const LoadingContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1e1b4b 0%, #581c87 50%, #be185d 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  
  > div {
    text-align: center;
  }
`;

export const LoadingSpinner = styled.div`
  width: 3rem;
  height: 3rem;
  border: 4px solid rgba(255, 255, 255, 0.2);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin: 0 auto 1rem auto;
`;

export const LoadingText = styled.p`
  color: white;
  font-size: 1.125rem;
  margin: 0;
`;